import { useEffect, type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../brand/BrandLogo';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api-client';
import { changeAppLanguage } from '../../i18n/i18n';
import { SUPPORTED_LOCALES, type AppLocale } from '../../i18n/localeTypes';
import { capturePartnerReferralCodeFromSearch } from '../../lib/partner-referrals';
import '../../styles/public-marketing.css';

interface PublicSiteChromeProps {
  children: ReactNode;
}

const DEMO_REQUEST_HREF = 'mailto:sales@ablecomply.com?subject=AbleComply%20Demo%20Request';
const SALES_CONTACT_HREF = 'mailto:sales@ablecomply.com';
const SUPPORT_CONTACT_HREF = 'mailto:support@ablecomply.com';

function PublicLink({
  href,
  children,
  className,
}: {
  href: string;
  children: ReactNode;
  className?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  if (href.startsWith('mailto:') || href.startsWith('/#') || href.startsWith('#')) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const targetUrl = new URL(href, window.location.origin);
    const currentPathAndSearch = `${location.pathname}${location.search}`;
    const targetPathAndSearch = `${targetUrl.pathname}${targetUrl.search}`;

    if (targetPathAndSearch !== currentPathAndSearch || targetUrl.hash) {
      return;
    }

    event.preventDefault();
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    if (location.hash) {
      void navigate({
        pathname: location.pathname,
        search: location.search,
        hash: '',
      }, { replace: true });
    }
  };

  return (
    <Link to={href} className={className} onClick={handleClick}>
      {children}
    </Link>
  );
}

export default function PublicSiteChrome({ children }: PublicSiteChromeProps) {
  const { t } = useTranslation('marketing');
  const { t: tCommon, i18n } = useTranslation('common');
  const { isAuthenticated, syncLocalePreferences } = useAuth();
  const location = useLocation();
  const productLinks = [
    { href: '/', label: t('footer.product.overview') },
    { href: '/#capabilities', label: t('footer.product.capabilities') },
    { href: '/#how-it-works', label: t('footer.product.howItWorks') },
    { href: '/#who-its-for', label: t('footer.product.whoItsFor') },
    { href: '/pricing', label: t('footer.product.pricing') },
  ];
  const companyLinks = [
    { href: '/about', label: t('footer.company.about') },
    { href: '/resources', label: t('footer.company.resources') },
    { href: '/methodology', label: t('footer.company.methodology') },
    { href: '/sample-report', label: t('footer.company.sampleReport') },
    { href: '/security', label: t('footer.company.security') },
    { href: '/accessibility-statement', label: t('footer.company.accessibilityStatement') },
    { href: '/contact', label: t('footer.company.contact') },
    { href: '/partners', label: t('footer.company.partners') },
  ];
  const supportLinks = [
    {
      href: isAuthenticated ? '/dashboard' : '/login',
      label: isAuthenticated ? t('footer.support.dashboard') : t('footer.support.signIn'),
    },
    ...(!isAuthenticated ? [{ href: '/register', label: t('footer.support.register') }] : []),
    { href: DEMO_REQUEST_HREF, label: t('footer.support.requestDemo') },
    { href: SALES_CONTACT_HREF, label: t('footer.support.sales') },
    { href: SUPPORT_CONTACT_HREF, label: t('footer.support.support') },
  ];
  const legalLinks = [
    { href: '/privacy-policy', label: t('footer.legal.privacy') },
    { href: '/terms-and-conditions', label: t('footer.legal.terms') },
    { href: '/cookie-policy', label: t('footer.legal.cookies') },
  ];
  const trustItems = [
    t('footer.trust.coverage'),
    t('footer.trust.evidence'),
    t('footer.trust.humanReview'),
  ];

  useEffect(() => {
    capturePartnerReferralCodeFromSearch(location.search);

    if (location.hash) {
      const targetId = location.hash.slice(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView();
        return;
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.hash, location.pathname, location.search]);

  const handleLanguageChange = async (nextLocale: string) => {
    await changeAppLanguage(nextLocale);

    if (!isAuthenticated) {
      return;
    }

    syncLocalePreferences({ preferredLocale: nextLocale as AppLocale });
    try {
      await api.patch('/auth/me/preferences', {
        preferredLocale: nextLocale,
      });
    } catch {
      // Locale still persists locally for public surfaces.
    }
  };

  return (
    <div className="acc-public-shell">
      <div className="acc-public-header-wrap">
        <div className="acc-public-frame acc-public-header">
          <Link to="/" className="acc-public-brand" aria-label={t('header.brandAriaLabel')}>
            <BrandLogo variant="marketing" tone="dark" responsive />
          </Link>

          <nav className="acc-public-nav" aria-label={t('header.navAriaLabel')}>
            <PublicLink href="/#capabilities">{t('header.nav.capabilities')}</PublicLink>
            <PublicLink href="/#how-it-works">{t('header.nav.howItWorks')}</PublicLink>
            <PublicLink href="/#who-its-for">{t('header.nav.whoItsFor')}</PublicLink>
            <PublicLink href="/partners">{t('header.nav.partners')}</PublicLink>
            <PublicLink href="/pricing">{t('header.nav.pricing')}</PublicLink>
          </nav>

          <div className="acc-public-actions">
            <label className="acc-public-locale">
              <select
                value={i18n.resolvedLanguage ?? i18n.language}
                onChange={(event) => void handleLanguageChange(event.target.value)}
                className="acc-public-locale-select"
              >
                {SUPPORTED_LOCALES.map((locale) => (
                  <option key={locale} value={locale}>
                    {tCommon(`locales.${locale as AppLocale}`)}
                  </option>
                ))}
              </select>
            </label>
            <PublicLink
              href={isAuthenticated ? '/dashboard' : '/login'}
              className="acc-public-button acc-public-button--ghost acc-public-button--text"
            >
              {isAuthenticated ? t('actions.openDashboard') : t('actions.signIn')}
            </PublicLink>
            <PublicLink href={DEMO_REQUEST_HREF} className="acc-public-button acc-public-button--primary">
              {t('actions.requestDemo')}
            </PublicLink>
          </div>
        </div>
      </div>

      <main className="acc-public-main">{children}</main>

      <footer className="acc-public-footer">
        <div className="acc-public-frame acc-public-footer-inner">
          <section className="acc-public-footer-brand" aria-label={t('header.brandAriaLabel')}>
            <div className="acc-public-footer-kicker">{t('footer.eyebrow')}</div>
            <BrandLogo variant="marketing" tone="dark" />
            <div className="acc-public-footer-copy">
              {t('footer.copy')}
            </div>
          </section>

          <nav className="acc-public-footer-section" aria-label={t('footer.productHeading')}>
            <div className="acc-public-footer-heading">{t('footer.productHeading')}</div>
            <ul className="acc-public-footer-list">
              {productLinks.map((link) => (
                <li key={link.href}>
                  <PublicLink href={link.href} className="acc-public-footer-link">
                    {link.label}
                  </PublicLink>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="acc-public-footer-section" aria-labelledby="public-footer-company-heading">
            <div className="acc-public-footer-heading" id="public-footer-company-heading">
              {t('footer.companyHeading')}
            </div>
            <p className="acc-public-footer-text">{t('footer.companyDescription')}</p>
            <ul className="acc-public-footer-list">
              {companyLinks.map((link) => (
                <li key={link.href}>
                  <PublicLink href={link.href} className="acc-public-footer-link">
                    {link.label}
                  </PublicLink>
                </li>
              ))}
            </ul>
          </nav>

          <section className="acc-public-footer-section" aria-labelledby="public-footer-support-heading">
            <div className="acc-public-footer-heading" id="public-footer-support-heading">
              {t('footer.supportHeading')}
            </div>
            <p className="acc-public-footer-text">{t('footer.supportDescription')}</p>
            <ul className="acc-public-footer-list">
              {supportLinks.map((link) => (
                <li key={link.href}>
                  <PublicLink href={link.href} className="acc-public-footer-link">
                    {link.label}
                  </PublicLink>
                </li>
              ))}
            </ul>
          </section>

          <nav className="acc-public-footer-section" aria-labelledby="public-footer-legal-heading">
            <div className="acc-public-footer-heading" id="public-footer-legal-heading">
              {t('footer.legalHeading')}
            </div>
            <p className="acc-public-footer-text">{t('footer.legalDescription')}</p>
            <ul className="acc-public-footer-list">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <PublicLink href={link.href} className="acc-public-footer-link">
                    {link.label}
                  </PublicLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="acc-public-frame acc-public-footer-meta">
          <section className="acc-public-footer-trust" aria-labelledby="public-footer-trust-heading">
            <div className="acc-public-footer-heading" id="public-footer-trust-heading">
              {t('footer.trustHeading')}
            </div>
            <p className="acc-public-footer-text">{t('footer.trustDescription')}</p>
            <ul className="acc-public-footer-list acc-public-footer-list--trust">
              {trustItems.map((item) => (
                <li key={item} className="acc-public-footer-trust-item">
                  <span className="acc-public-footer-trust-mark" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <div className="acc-public-footer-disclaimer">
            {t('footer.disclaimer')}
          </div>
        </div>

        <div className="acc-public-frame acc-public-footer-bottom">
          <div className="acc-public-footer-bottom-line">
            {t('footer.legalLine')}
          </div>
        </div>
      </footer>
    </div>
  );
}
